// PRC-018 · D1 (prueba terminada, administrador), D2 (miembro) y D3 (suscripción terminada).
import React, { useState } from 'react';
import { Box } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../../../context/AuthContext';
import { SimpleLayout, Avatar } from '../components/JourneyLayouts';
import { Notice, PlanCard, StateIcon } from '../components/JourneyBlocks';
import { Eyebrow, JourneyButton, JourneyCard, JourneyChip, JourneyText } from '../components/JourneyBasics';
import { J } from '../journeyTokens';
import { formatLongDate, openBillingPortal, PLAN_LABEL, startCheckout, SUPPORT_EMAIL } from '../journeyBilling';

const KeyCard: React.FC<{ k: string; v: React.ReactNode; d?: React.ReactNode }> = ({ k, v, d }) => (
  <JourneyCard tight>
    <Box sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: J.muted }}>{k}</Box>
    <Box sx={{ fontSize: 16, fontWeight: 700, color: J.text, mt: '6px' }}>{v}</Box>
    {d && <JourneyText variant="small" sx={{ mt: '4px' }}>{d}</JourneyText>}
  </JourneyCard>
);

// D1: planes con el contenido aprobado en el boceto (Autonomy, «Hablar con ventas»).
const PlanChooser: React.FC<{ onError: (msg: string) => void }> = ({ onError }) => {
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      await startCheckout(yearly ? 'growth_yearly' : 'growth_monthly');
    } catch (err: any) {
      setBusy(false);
      onError(err?.response?.status === 409
        ? 'Ya tienes una suscripción abierta: gestiónala desde el portal de facturación.'
        : 'No se pudo abrir el pago. Inténtalo de nuevo.');
    }
  };
  const toggleItem = (on: boolean, label: string, onClick: () => void) => (
    <Box component="button" type="button" onClick={onClick} aria-pressed={on} sx={{ border: 0, cursor: 'pointer', px: 2, py: 1, borderRadius: 999, fontSize: 13, fontWeight: 700, fontFamily: J.font, bgcolor: on ? J.border : 'transparent', color: on ? J.text : J.muted }}>{label}</Box>
  );
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <JourneyText variant="h2">Elige un plan para continuar</JourneyText>
        <Box role="group" aria-label="Periodo de facturación" sx={{ display: 'inline-flex', p: '4px', borderRadius: 999, border: `1px solid ${J.borderStrong}`, bgcolor: J.panel, gap: '4px' }}>
          {toggleItem(!yearly, 'Mensual', () => setYearly(false))}
          {toggleItem(yearly, 'Anual · 2 meses gratis', () => setYearly(true))}
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))' }, gap: '20px' }}>
        <PlanCard
          featured
          badge="Recomendado"
          name="Growth"
          price={yearly ? '9.900 €' : '990 €'}
          period={yearly ? '+ IVA / año' : '+ IVA / mes'}
          description="Margin Center, Control comercial y Optimización para tu equipo."
          features={[]}
          action={<JourneyButton block disabled={busy} onClick={buy}>Contratar Growth</JourneyButton>}
        />
        <PlanCard
          name="Autonomy"
          price={yearly ? '14.900 €' : '1.490 €'}
          period={yearly ? '+ IVA / año' : '+ IVA / mes'}
          description="Todo Growth, con integración con tu ERP y consultor asignado."
          features={[]}
          action={<JourneyButton kind="ghost" block href={`mailto:${SUPPORT_EMAIL}?subject=Autonomy`}>Hablar con ventas</JourneyButton>}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, fontSize: 13, color: J.muted, '& a': { color: J.skyText, fontWeight: 600, textDecoration: 'none' } }}>
        <span>Precios sin IVA.</span>
        <a href="/terms">Términos</a>
        <a href="/privacy">Privacidad</a>
      </Box>
    </>
  );
};

const PricingBlockedScreen: React.FC = () => {
  const { user, signOut, pricingRole, pricingBlock, pricingAccountName, pricingAccountAdmin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const name = (user as any)?.full_name || user?.email || null;
  const userLabel = [name, pricingAccountName].filter(Boolean).join(' · ') || null;
  const onSignOut = () => { void signOut(); };
  const reason = pricingBlock?.reason ?? 'unavailable';

  const portal = async () => {
    setBusy(true);
    try {
      await openBillingPortal();
    } catch {
      setBusy(false);
      setError('No se pudo abrir el portal de facturación. Inténtalo de nuevo.');
    }
  };

  // D2: miembro sin acceso.
  if (pricingRole !== 'admin') {
    const admin = pricingAccountAdmin;
    return (
      <SimpleLayout userLabel={userLabel} onSignOut={onSignOut} narrow>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '24px' }}>
          <StateIcon tone="warn"><LockOutlinedIcon /></StateIcon>
          <Eyebrow both>Acceso en pausa</Eyebrow>
          <JourneyText variant="title">La cuenta de tu empresa no está activa</JourneyText>
          <JourneyText variant="lead">
            La suscripción de <b style={{ color: J.text }}>{pricingAccountName ?? 'tu empresa'}</b> no está al día. Solo el administrador de la cuenta puede reactivarla; tu trabajo y los datos se conservan.
          </JourneyText>
          {admin && (
            <JourneyCard tight sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Avatar name={admin.full_name || admin.email} />
                <Box sx={{ display: 'flex', flexDirection: 'column', fontSize: 13, lineHeight: 1.3 }}>
                  <b>{admin.full_name || admin.email}</b>
                  <Box component="span" sx={{ color: J.muted }}>Administrador{admin.email ? ` · ${admin.email}` : ''}</Box>
                </Box>
              </Box>
              {admin.email && (
                <JourneyButton small href={`mailto:${admin.email}`}>
                  Escribir a {(admin.full_name || '').split(' ')[0] || 'tu administrador'}
                </JourneyButton>
              )}
            </JourneyCard>
          )}
          <JourneyText variant="small">Cuando la suscripción vuelva a estar activa podrás entrar con normalidad, sin hacer nada más.</JourneyText>
        </Box>
      </SimpleLayout>
    );
  }

  // D3: administrador tras una suscripción.
  if (reason.startsWith('subscription_')) {
    const planLabel = pricingBlock?.plan ? PLAN_LABEL[pricingBlock.plan] : '';
    const reasonLabel = reason === 'subscription_unpaid' ? 'No se pudo cobrar la renovación' : reason === 'subscription_canceled' ? 'Cancelada por ti' : 'Suscripción terminada';
    return (
      <SimpleLayout userLabel={userLabel} onSignOut={onSignOut}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: 700 }}>
          <StateIcon tone="warn"><WarningAmberIcon /></StateIcon>
          <Eyebrow>Suscripción terminada</Eyebrow>
          <JourneyText variant="title">
            Tu suscripción{planLabel ? ` ${planLabel}` : ''} terminó{pricingBlock?.ended_at ? ` el ${formatLongDate(pricingBlock.ended_at)}` : ''}
          </JourneyText>
          <JourneyText variant="lead">El acceso está en pausa. Puedes volver cuando quieras: tus datos, avisos y decisiones siguen guardados.</JourneyText>
        </Box>
        {error && <Notice tone="danger">{error}</Notice>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: '16px' }}>
          <KeyCard k="Motivo" v={reasonLabel} />
          <KeyCard k="Datos guardados hasta" v={pricingBlock?.data_retained_until ? formatLongDate(pricingBlock.data_retained_until) : '—'} d="90 días desde el fin de la suscripción." />
          <KeyCard k="Facturas" v={<Box component="button" type="button" onClick={portal} disabled={busy} sx={{ all: 'unset', cursor: 'pointer', color: J.skyText, fontWeight: 600 }}>Ver y descargar</Box>} d="Siguen disponibles en el portal de Stripe." />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {pricingBlock?.reactivate_in_portal
            ? <JourneyButton disabled={busy} onClick={portal}>Reactivar suscripción</JourneyButton>
            : <JourneyButton href="/subscription">Reactivar suscripción</JourneyButton>}
          <JourneyButton kind="ghost" href={`mailto:${SUPPORT_EMAIL}`}>Hablar con nosotros</JourneyButton>
        </Box>
      </SimpleLayout>
    );
  }

  // D1: administrador tras la prueba, o que nunca tuvo prueba.
  const hadTrial = reason === 'trial_ended' && !!pricingBlock?.ended_at;
  return (
    <SimpleLayout userLabel={userLabel} onSignOut={onSignOut}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '32px', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: 620 }}>
          <StateIcon tone="warn"><LockOutlinedIcon /></StateIcon>
          <Eyebrow>{hadTrial ? 'Prueba terminada' : 'Sin plan activo'}</Eyebrow>
          <JourneyText variant="title">
            {hadTrial ? `Tu prueba gratuita terminó el ${formatLongDate(pricingBlock!.ended_at)}` : 'Elige un plan para empezar'}
          </JourneyText>
          <JourneyText variant="lead">
            {hadTrial
              ? 'El acceso a la aplicación está en pausa hasta que elijas un plan. Al contratar, todo vuelve tal como lo dejaste.'
              : 'Para usar PROA Pricing necesitas un plan activo.'}
          </JourneyText>
        </Box>
        {hadTrial && pricingBlock?.data_retained_until && (
          <JourneyCard tight sx={{ flex: '0 1 360px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <JourneyText variant="h3">Tus datos</JourneyText>
            <JourneyText variant="body">Se conservan hasta el <b style={{ color: J.text }}>{formatLongDate(pricingBlock.data_retained_until)}</b>.</JourneyText>
            <JourneyText variant="small">Si no vas a contratar y quieres que los borremos antes, escríbenos a {SUPPORT_EMAIL}.</JourneyText>
          </JourneyCard>
        )}
      </Box>
      {error && <Notice tone="danger">{error}</Notice>}
      <PlanChooser onError={setError} />
      {reason === 'unavailable' && <JourneyChip tone="neutral">Si crees que es un error, escríbenos a {SUPPORT_EMAIL}.</JourneyChip>}
    </SimpleLayout>
  );
};

export default PricingBlockedScreen;
